'use client';

import { useCallback, useEffect, useState } from 'react';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { useCookies } from 'next-client-cookies';
import { CategoryBarData } from 'shared/types/product/Category';
import { ProductPaginatedResult } from 'shared/types/result';
import Header from 'components/commercetools-ui/organisms/header';
import { HeaderProps } from 'components/commercetools-ui/organisms/header/types';
import { HeaderContentProps } from 'components/commercetools-ui/organisms/header/types/gmsb-header';
import MaintenanceBar, { Props as MaintenanceBarProps } from 'components/commercetools-ui/organisms/maintenance-bar';
import { mapCategotry } from 'helpers/entity-mappers/map-category';
import { useDebounce } from 'helpers/hooks/useDebounce';
import { useCategoryBar } from 'providers/categoryBar';
import { LineItem as CartLineItem } from 'types/entity/cart';
import { Product } from 'types/entity/product';
import { LineItem as WishlistLineItem } from 'types/entity/wishlist';
import { useCart, useProduct, useWishlist } from 'frontastic/hooks';
import { TasticProps } from '../types';
import { getProductsWithMatchingVariants } from './helper';

const HeaderTastic = ({ data, categories }: TasticProps<HeaderProps & MaintenanceBarProps & HeaderContentProps>) => {
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const [queryTerm, setQueryTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const debouncedQuery = useDebounce(queryTerm, 200);
  const { query, getSearchTerms } = useProduct();
  const [recommendations, setRecommendations] = useState<Product[]>([]);

  useEffect(() => {
    const localeRefined = locale
      ? locale
          .split('-')
          .map((part, index) => (index === 1 ? part.toUpperCase() : part.toLowerCase()))
          .join('-')
      : 'en-US';

    if (debouncedQuery && debouncedQuery.length >= (data?.minSearchChars ?? 0)) {
      query({
        query: debouncedQuery,
        limit: data?.productSuggestions,
        sortAttributes: [
          { 'variants.attributes.display_name_universal': 'asc' },
          { 'variants.attributes.short_description_universal': 'asc' },
        ],
      }).then((res: any) => {
        if (!res.isError && res?.data?.items) {
          setProducts(res?.data?.items);
        } else {
          setProducts([]);
        }
      });
    } else {
      setProducts([]);
    }

    if (debouncedQuery && debouncedQuery.length >= (data?.minSearchChars ?? 0)) {
      getSearchTerms({
        searchTerm: debouncedQuery,
        locale: localeRefined,
      }).then((res: any) => {
        if (res.isError) {
          setKeywords([]);
        } else {
          setKeywords(res?.data?.suggestions);
        }
      });
    } else {
      setKeywords([]);
    }
  }, [debouncedQuery, cookies]);

  useEffect(() => {
    void (async function name() {
      const skus = data.recommendation?.filter((item) => item.recommendationSKU).map((item) => item.recommendationSKU);

      const res: SDKResponse<ProductPaginatedResult> = await query({
        skus,
      });
      if (!res.isError && res?.data?.items) {
        setRecommendations(getProductsWithMatchingVariants(res.data.items as Product[], skus ?? []));
      } else {
        setRecommendations([]);
      }
    })();
  }, []);

  const { categoryBarData, announcementBarData } = useCategoryBar();
  const { addItem, removeItem, updateItem, redeemDiscountCode, removeDiscountCode, miniCarts } = useCart();

  const {
    data: wishlist,
    addToWishlist,
    totalItems: totalWishlistItems,
    removeLineItem,
    deleteWishlist,
  } = useWishlist();

  const moveToWishlist = useCallback(
    async (lineItem: CartLineItem) => {
      if (!wishlist) return;

      await Promise.all([
        removeItem(lineItem.lineItemId as string),
        addToWishlist(
          wishlist,
          {
            lineItemId: lineItem.lineItemId ?? '',
            productId: lineItem.productId,
            name: lineItem.name,
            type: lineItem.type,
            count: 1,
            variant: lineItem.variant,
            addedAt: new Date(),
            _url: lineItem._url,
          },
          1,
        ),
      ]);
    },
    [removeItem, addToWishlist, wishlist],
  );

  const maintenanceData = {
    activateMaintenance: data.activateMaintenance,
    maintenanceText: data.maintenanceText,
  };

  return (
    <div id="header-container" className="fixed top-0 z-50 w-full">
      {maintenanceData.activateMaintenance && <MaintenanceBar {...maintenanceData} />}
      <Header
        {...data}
        carts={miniCarts}
        categories={categories.map((category) => mapCategotry(category, { locale }))}
        onApplyDiscountCode={redeemDiscountCode}
        onRemoveDiscountCode={removeDiscountCode}
        onRemoveItem={removeItem}
        onUpdateItem={updateItem}
        OnMoveToWishlist={moveToWishlist}
        wishlist={wishlist}
        onRemoveFromWishlist={async (lineItemId) => {
          if (wishlist) {
            await removeLineItem(
              wishlist,
              wishlist.lineItems?.find((item) => item.lineItemId === lineItemId) as WishlistLineItem,
            );
          }
        }}
        onMoveToCart={async (lineItem: WishlistLineItem) => {
          if (wishlist && lineItem.variant) {
            await removeLineItem(
              wishlist,
              wishlist.lineItems?.find((item) => item.lineItemId === lineItem.lineItemId) as WishlistLineItem,
            );

            await addItem(lineItem.variant, 1);
          }
        }}
        onClearWishlist={async () => {
          if (wishlist) deleteWishlist(wishlist);
        }}
        totalWishlistItems={totalWishlistItems}
        navLinks={categoryBarData?.map(
          (category: CategoryBarData) => category,
        ) ?? []
        }
        logo={data.logo}
        logoLink={data.logoLink}
        logoMobile={data.logoMobile}
        logoLinkMobile={data.logoLinkMobile}
        tiles={data.tiles}
        searchItems={debouncedQuery.length > 0 ? (products as any) : []}
        recommendations={recommendations ?? []}
        searchKeywords={debouncedQuery.length > 0 ? (keywords as any) : []}
        onSearchQueryUpdate={(q) => setQueryTerm(q)}
        emptyCartTitle={data.emptyCartTitle}
        emptyCartSubtitle={data.emptyCartSubtitle}
        emptyCartImage={data.emptyCartImage}
        emptyCartCategories={data.emptyCartCategories}
        emptyWishlistTitle={data.emptyWishlistTitle}
        emptyWishlistSubtitle={data.emptyWishlistSubtitle}
        emptyWishlistImage={data.emptyWishlistImage}
        emptyWishlistCategories={data.emptyWishlistCategories}
        contentConfig={data}
        announcementBarData={announcementBarData}
      />
    </div>
  );
};
export default HeaderTastic;
